export type TrustScoreScale = 5 | 100;
export const RATING_INPUT_MAX: TrustScoreScale = 100; // Set to 100 when backend provides 0-100 scores
export const STAR_DISPLAY_MAX: number = 5; // Number of stars to render

/**
 * Which star icons render filled/half/empty, and the bucket `fnRatingDisplay`
 * (widget-1..14) sorts a score into. Original nearest-half-star rounding —
 * left as-is so widget-1..14's label/color bucketing keeps its existing
 * behavior; only `scoreToDisplayStars` below was added to fix the printed
 * number on widget-15..31 (the widget-v2.js preview templates).
 */
export const normalizeScoreToStars = (rawScore: number): number => {
  if (typeof rawScore !== "number" || Number.isNaN(rawScore)) return 0;
  const clamped = Math.max(0, Math.min(rawScore, RATING_INPUT_MAX));
  const stars = (clamped / RATING_INPUT_MAX) * STAR_DISPLAY_MAX;
  return Math.round(stars * 2) / 2;
};

/**
 * The printed rating number (e.g. "2.1"). Matches widget-v2.js's
 * `starsValueOf` exactly — one-decimal precision, not snapped to the
 * half-star grid `normalizeScoreToStars` uses for icon fill, plus the same
 * epsilon nudge widget-v2.js documents: 42/20 stores as 2.0999999999999996
 * in IEEE754, so a plain `.toFixed(1)` would print "2.0" instead of "2.1"
 * without it.
 */
export const scoreToDisplayStars = (rawScore: number): string => {
  if (typeof rawScore !== "number" || Number.isNaN(rawScore)) return "0.0";
  const clamped = Math.max(0, Math.min(rawScore, RATING_INPUT_MAX));
  const stars = (clamped / RATING_INPUT_MAX) * STAR_DISPLAY_MAX;
  return (Math.round(stars * 10 + 1e-6) / 10).toFixed(1);
};
